import { Component, type ErrorInfo, type ReactNode } from "react";

import { reportClientError } from "../lib/client-error.js";
import { logger } from "../lib/logger.js";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  componentStack: string | null;
}

/**
 * Top-level boundary that prevents the whole app from rendering as a
 * blank page when something throws during render. Two jobs:
 *
 * 1. Show the error on screen — including the stack — so a user (or
 *    the developer over their shoulder) can read what blew up. The
 *    Mini App runs in a Telegram WebView where remote devtools are
 *    spotty; making the error visible in-app is often the only signal.
 * 2. Ship the error to the bot's /api/client-error endpoint so it
 *    appears in cluster logs alongside other miniapp errors.
 *
 * We deliberately do not auto-recover or rerender on transient errors;
 * the user sees a "Reload" button instead. Auto-retrying a failed
 * render typically just re-throws and produces a flicker loop.
 */
export class RootErrorBoundary extends Component<Props, State> {
  state: State = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.setState({ componentStack: info.componentStack ?? null });
    logger.error("react render error", error);
    reportClientError({
      source: "boundary",
      message: error.message,
      stack:
        // Combine the JS stack with React's component stack — the latter
        // is often what tells you which feature crashed.
        info.componentStack
          ? `${error.stack ?? ""}\n--- componentStack ---${info.componentStack}`
          : error.stack,
      route:
        typeof window === "undefined" ? undefined : window.location.pathname,
    });
  }

  reload = (): void => {
    if (typeof window !== "undefined") window.location.reload();
  };

  render(): ReactNode {
    const { error, componentStack } = this.state;
    if (!error) return this.props.children;

    const stack = error.stack ?? `${error.name}: ${error.message}`;

    return (
      <div
        style={{
          padding: "16px",
          minHeight: "100dvh",
          background: "#1a1a1a",
          color: "#f5f5f5",
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          fontSize: "13px",
          overflow: "auto",
        }}
      >
        <h1 style={{ fontSize: "16px", margin: "0 0 8px", color: "#ff6b6b" }}>
          Something broke
        </h1>
        <p style={{ margin: "0 0 12px", color: "#bbb" }}>
          The Mini App hit an unexpected error and stopped rendering. The
          error has been reported. You can try reloading.
        </p>
        <button
          type="button"
          onClick={this.reload}
          style={{
            background: "#2481cc",
            color: "#fff",
            border: "none",
            borderRadius: "6px",
            padding: "8px 16px",
            fontSize: "13px",
            fontWeight: 500,
            cursor: "pointer",
            marginBottom: "16px",
          }}
        >
          Reload
        </button>
        <details open style={{ fontFamily: "monospace", fontSize: "11px" }}>
          <summary style={{ cursor: "pointer", marginBottom: "8px" }}>
            Stack trace
          </summary>
          <pre
            style={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              background: "#0a0a0a",
              padding: "10px",
              borderRadius: "4px",
              color: "#ddd",
            }}
          >
            {stack}
            {componentStack && `\n--- componentStack ---${componentStack}`}
          </pre>
        </details>
      </div>
    );
  }
}
